import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, Col, Empty, Pagination, Row, Typography } from "antd";
import { CalendarOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { helper } from "@src/controls/controlHelper";
import Loader from "@src/components/Loading";
import dayjs from "dayjs";
import TicketDetailCtrl from "./TicketDetailCtrl";

const { Text } = Typography;

const PAGE_ID = 147;

interface TicketItem {
  id: number;
  nameEvent: string;
  image: string;
  startDateEvent: number;
  presaleStart: number;
  presaleEnd: number;
  stadium?: {
    name: string;
  };
  [key: string]: any;
}

const TicketSalesCtrl: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [pageInfo, setPageInfo] = useState<any>(null);
  const [items, setItems] = useState<TicketItem[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const pageInfoRef = useRef<any>(null);

  const loadData = useCallback(
    async (_pageInfo?: any) => {
      try {
        const info =
          _pageInfo || pageInfoRef.current || (await helper.getPage(PAGE_ID));
        if (!pageInfoRef.current) {
          pageInfoRef.current = info;
          setPageInfo(info);
        }

        const rs: any = await helper.callPageApi(info, "find", {
          limit: pageSize,
          skip: pageSize * (currentPage - 1),
          sort: JSON.stringify([{ id: "desc" }]),
        });

        const responseData = rs?.data?.data || rs?.data || {};
        const dataItems =
          responseData.items || responseData.data || responseData;
        const dataTotal =
          responseData.pagination?.total || responseData.count || 0;

        setItems(Array.isArray(dataItems) ? dataItems : []);
        setTotal(dataTotal);
      } catch (error) {
        console.error(error);
      }
    },
    [currentPage, pageSize]
  );

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadData();
      setLoading(false);
    })();
  }, [loadData]);

  const getStatusText = (item: any) => {
    const now = new Date().getTime();
    if (now < item.presaleStart) {
      return { text: "Sắp mở bán", color: "#FF9900", isValid: false };
    }
    if (now > item.presaleEnd) {
      return { text: "Đã ngừng bán", color: "#FF0000", isValid: false };
    }
    return { text: "Đang diễn ra", color: "#13A633", isValid: true };
  };

  const renderCard = (item: TicketItem) => {
    const status = getStatusText(item);

    return (
      <Card
        key={item.id}
        hoverable
        onClick={() => {
          if (status.isValid) {
            setSelectedEventId(item.id);
          }
        }}
        bodyStyle={{
          padding: "18px 20px 20px",
          display: "flex",
          flex: 1,
          flexDirection: "column",
        }}
        style={{
          borderRadius: 12,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          marginBottom: 16,
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          height: "100%",
        }}
        cover={
          <div
            style={{
              position: "relative",
              backgroundColor: "#f8f9fa",
              textAlign: "center",
              height: 240,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              alt={item.nameEvent}
              src={item.image}
              style={{
                maxWidth: "82%",
                maxHeight: "82%",
                objectFit: "contain",
                display: "block",
              }}
            />
            {/* Bookmark Ribbon */}
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                backgroundColor: status.color,
                color: "white",
                padding: "8px 12px 14px 12px",
                fontWeight: 600,
                fontSize: 12,
                lineHeight: 1.25,
                zIndex: 2,
                clipPath: "polygon(0 0, 100% 0, 100% 100%, 50% 85%, 0 100%)",
                textAlign: "center",
                minWidth: 104,
              }}
            >
              {status.text}
            </div>
          </div>
        }
      >
        <div style={{ marginBottom: 18, minHeight: 48 }}>
          <Text
            strong
            style={{
              color: "#4a4a4a",
              display: "-webkit-box",
              fontSize: 16,
              lineHeight: 1.5,
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
          >
            {item.nameEvent}
          </Text>
        </div>

        <div
          style={{
            display: "grid",
            gap: 12,
            marginTop: "auto",
          }}
        >
          <div
            style={{
              alignItems: "center",
              display: "grid",
              gap: 10,
              gridTemplateColumns: "22px minmax(0, 1fr)",
              minWidth: 0,
            }}
          >
            <CalendarOutlined style={{ fontSize: 18, color: "#8c8c8c" }} />
            <Text type="secondary" style={{ fontSize: 14 }}>
              {dayjs(item.startDateEvent).format("DD/MM/YYYY HH:mm")}
            </Text>
          </div>
          <div
            style={{
              alignItems: "center",
              display: "grid",
              gap: 10,
              gridTemplateColumns: "22px minmax(0, 1fr)",
              minWidth: 0,
            }}
          >
            <EnvironmentOutlined style={{ fontSize: 18, color: "#8c8c8c" }} />
            <Text
              type="secondary"
              style={{
                display: "block",
                fontSize: 14,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.stadium?.name || "-"}
            </Text>
          </div>
          <div>
            <Button
              type={status.isValid ? "primary" : "default"}
              disabled={!status.isValid}
              block
              style={{
                borderRadius: 6,
                backgroundColor: !status.isValid ? "#6b7280" : undefined,
                color: !status.isValid ? "white" : undefined,
                border: "none",
                height: 40,
              }}
              onClick={() => {
                if (status.isValid) {
                  setSelectedEventId(item.id);
                }
              }}
            >
              Bán vé
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  if (selectedEventId) {
    return (
      <TicketDetailCtrl
        eventId={selectedEventId}
        pageInfo={pageInfo}
        onBack={() => setSelectedEventId(null)}
      />
    );
  }

  if (loading) return <Loader />;

  return (
    <div style={{ padding: "16px 0" }}>
      <Row gutter={[16, 16]}>
        {items.length === 0 ? (
          <Col span={24}>
            <Empty description="Không có sự kiện bán vé nào" />
          </Col>
        ) : (
          items.map((item) => (
            <Col key={item.id} xs={24} sm={12} lg={8} xl={6}>
              {renderCard(item)}
            </Col>
          ))
        )}

        {total > 0 && (
          <Col span={24}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 16,
                width: "100%",
              }}
            >
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={total}
                showSizeChanger
                hideOnSinglePage
                onChange={(page, size) => {
                  setCurrentPage(page || 1);
                  setPageSize(size || pageSize);
                }}
              />
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default TicketSalesCtrl;
